import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { Helmet } from 'react-helmet';
import { Link, Redirect } from 'react-router-dom';
import styled from 'styled-components';
import { palette } from 'theme';
import _ from 'lodash';

import { getOrderDetails, setActiveStep } from 'features/checkout/actions';
import { getContentStackData as getCartStack } from 'features/cart/actions';
import { getDistributorContentStackData as getDistributorStack } from 'features/distributor/actions';

import Button from '@material-ui/core/Button';
import Card from '@material-ui/core/Card';
import Stepper from '@material-ui/core/Stepper';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';

import Container from 'components/Container';
import SwiftypeTags from 'components/SwiftypeTags';
import ContentTypography from 'components/ContentTypography';

import AddressSelector from './elements/AddressSelector';
import ShippingSelector from './elements/ShippingSelector';
import CheckoutStep from './elements/CheckoutStep';
import OrderItems from './elements/OrderItems';

import { distributorLockedCountries } from 'constants/Countries';
import DistributorModal from '../Layout/Header/elements/DistributorModal';
import { hfCas12MaxBlockedCountries } from 'src/features/order/sgrna/constants';

const CheckoutHeader = styled(Typography)`
  margin-top: 60px;
`;

const formatAddress = address => {
  return address
    ? `${address.company_name} - ${address.first_last} (${
        address.notification_email
      }) ${address.street_address_1}${address.street_address_2 && ' '}${
        address.street_address_2
      }, ${address.city_state_zip}, ${address.country_name}`
    : '';
};

export const DistributorMessage = ({ entry }) => (
  <Typography variant="caption">
    {entry ? (
      <>
        Your region is managed by a Synthego distributor, please contact them to
        place an order:
        <br />{' '}
        <a href={'http://' + entry.website['href']}>
          {' '}
          {entry.website['title']}{' '}
        </a>
        <br /> <a href={'mailto:' + entry.email}> {entry.email} </a>
        <br />{' '}
        <a href={'tel:' + entry.telephone_number}> {entry.telephone_number} </a>
        <br /> {entry.fax_number} <br /> {entry.name}
        <br /> {entry.address}
        <br /> {entry.address_2}
        <br /> {entry.country_name}
      </>
    ) : (
      <>
        You are in a distributor managed region, please contact your distributor
        or Synthego support for help ordering!
        <br />
        <br />
        <DistributorModal displayText="Distributor Details" />
      </>
    )}
  </Typography>
);

export const HfCas12MaxMessage = () => {
  return (
    <Typography variant="caption">
      The region you are attempting to ship to does not support hfCas12Max
      products, please contact Synthego support for help ordering!
    </Typography>
  );
};

export const MixedCartMessage = () => {
  return (
    <Typography variant="caption">
      Synthego.com no longer supports ordering EditCo products. Please remove
      EditCo products from your cart to continue. You can order EditCo products
      at <a href="http://www.editco.bio">Editco.bio</a>!
    </Typography>
  );
};

const isDistributorLocked = (order, isLocked) => {
  return (
    isLocked &&
    !order.is_distributor &&
    (distributorLockedCountries.includes(
      getCountryCode(order.billing_address)
    ) ||
      distributorLockedCountries.includes(
        getCountryCode(order.shipping_address)
      ))
  );
};

const isHfCas12MaxLocked = order => {
  return (
    order.has_hfcas12max_items &&
    (hfCas12MaxBlockedCountries.includes(
      getCountryCode(order.billing_address)
    ) ||
      hfCas12MaxBlockedCountries.includes(
        getCountryCode(order.shipping_address)
      ))
  );
};

const getCountryCode = address => {
  return address ? `${address.country}` : '';
};

const shippingChoiceLabels = {
  API: 'API',
  CUSTOM: 'Your Carrier Account with ',
  FLAT: 'Flat Rate via '
};

export const NotePaper = styled(Paper)`
  background: ${palette.grey[50]};
  padding: 12px 24px 24px 24px;
  margin: 24px 0;
`;

const CheckoutContainer = styled(Container)`
  margin-bottom: 40px;
`;

const Checkout = ({
  match,
  order,
  loading,
  error,
  activeStep,
  allStepsComplete,
  cartStack,
  distributorStack,
  isLocked,
  orderShouldUpdate,
  getOrderDetails,
  getCartStack,
  getDistributorStack,
  setActiveStep,
  history
}) => {
  useEffect(
    () => {
      getOrderDetails(match.params.orderToken);
      getCartStack('/cart');
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [match.params.orderToken]
  );

  useEffect(
    () => {
      if (orderShouldUpdate) {
        getOrderDetails(match.params.orderToken);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [orderShouldUpdate]
  );

  useEffect(
    () => {
      if (Boolean(order.shipping_address) || Boolean(order.billing_address)) {
        if (
          distributorLockedCountries.includes(
            getCountryCode(order.shipping_address)
          )
        ) {
          getDistributorStack(getCountryCode(order.shipping_address));
        } else {
          getDistributorStack(getCountryCode(order.billing_address));
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [order.shipping_address, order.billing_address, getDistributorStack]
  );
  const shippingMethodName = _.get(order, 'groups[0].shipping_method_name');
  const shippingProvider = _.get(order, 'groups[0].flat_shipping_carrier');
  const shippingCarrier = _.get(order, 'groups[0].shipping_carrier');
  let shippingLabel = shippingChoiceLabels[shippingMethodName];
  if (shippingMethodName === 'FLAT') {
    shippingLabel += shippingProvider;
  }
  if (shippingMethodName === 'CUSTOM') {
    shippingLabel += shippingCarrier;
  }
  const mixedCart = () => {
    const items = order?.groups?.[0].items;
    return items && items.some(item => item.business_unit === 'Eclipse');
  };

  return error ? (
    <>
      <Typography variant="h3" align="center" gutterBottom>
        Error Loading Order
      </Typography>
      <Typography variant="body2" align="center" gutterBottom>
        {error.detail}
      </Typography>
    </>
  ) : order.status && !(order.status === 'new' || order.status === 'cart') ? (
    <Redirect to={`/order/summary/${order.token}`} />
  ) : (
    <>
      <Helmet title={`Synthego | Checkout`} />
      <SwiftypeTags noIndex />

      <CheckoutContainer>
        <CheckoutHeader variant="h3" gutterBottom>
          Checkout
        </CheckoutHeader>
        <Grid container spacing={40}>
          <Grid item xs={12} md={8}>
            <Card>
              <Stepper activeStep={activeStep} orientation="vertical">
                <CheckoutStep
                  completed={Boolean(order.shipping_address)}
                  label="Shipping Address"
                  completedLabel={formatAddress(order.shipping_address)}
                >
                  <AddressSelector type="shipping" />
                </CheckoutStep>
                <CheckoutStep
                  completed={Boolean(order.billing_address)}
                  label="Billing Address"
                  completedLabel={formatAddress(order.billing_address)}
                >
                  <AddressSelector type="billing" />
                </CheckoutStep>
                <CheckoutStep
                  completed={Boolean(
                    _.get(order, 'groups[0].shipping_method_set')
                  )}
                  label="Shipping Method"
                  completedLabel={`${shippingLabel} ${_.get(
                    order,
                    'groups[0].carrier_account_number'
                  )}`}
                >
                  <ShippingSelector />
                </CheckoutStep>
              </Stepper>
            </Card>
            <NotePaper elevation={0}>
              <ContentTypography
                variant="caption"
                dangerouslySetInnerHTML={{ __html: cartStack.bottom_text }}
                gutterBottom
              />
            </NotePaper>
          </Grid>
          <Grid item xs={12} md={4}>
            <OrderItems {...order} isLocked={isLocked} />
            {isDistributorLocked(order, isLocked) ||
            isHfCas12MaxLocked(order) ||
            mixedCart() ? (
              <NotePaper elevation={0}>
                {isDistributorLocked(order, isLocked) && (
                  <DistributorMessage entry={distributorStack} />
                )}
                {isHfCas12MaxLocked(order) && <HfCas12MaxMessage />}
                {mixedCart() && <MixedCartMessage />}
              </NotePaper>
            ) : (
              allStepsComplete && (
                <Button
                  id="summary-button"
                  component={Link}
                  to={`/order/summary/${order.token}`}
                  color="primary"
                  size="large"
                  variant="contained"
                  fullWidth
                >
                  Continue to Summary
                </Button>
              )
            )}
          </Grid>
        </Grid>
      </CheckoutContainer>
    </>
  );
};

export default connect(
  state => ({
    activeStep: state.checkout.activeStep,
    allStepsComplete: state.checkout.allStepsComplete,
    order: state.checkout.order,
    cartStack: state.cart.stack,
    distributorStack: state.distributor.distributorStack,
    isLocked: state.distributor.isLocked,
    loading: state.checkout.orderLoading,
    error: state.checkout.error,
    orderShouldUpdate: state.checkout.orderShouldUpdate
  }),
  {
    getOrderDetails,
    getCartStack,
    getDistributorStack,
    setActiveStep
  }
)(Checkout);
